import { useCallback, useEffect, useState } from 'react';
import {
    CreditCard, Crown, ArrowUpRight, Download, Check, Gift,
    CalendarClock, Bot, MessageSquare, ChevronRight, FileText, Ban,
} from 'lucide-react';
import { getSubscriptionDetails, getInvoices } from '@/hooks/useSubscription';
import { useStores } from '@/stores/RootStoreContext';
import { ModalEnum } from '@/interfaces';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';
import LogoSVG from '@/assets/logo.svg?react';

const statusLabels = { pending: 'Pendente', paid: 'Pago', cancelled: 'Cancelado' };
const statusVariants = { paid: 'success', pending: 'warning', cancelled: 'danger' };

/* ─── Skeleton ──────────────────────────────────── */
function Skeleton({ className, style }) {
    return (
        <div
            className={cn('animate-pulse rounded-xl', className)}
            style={{ background: 'var(--dash-surface-hover)', ...style }}
        />
    );
}

function SubscriptionSkeleton() {
    return (
        <div className="flex flex-col gap-5">
            {/* Plan hero */}
            <Card>
                <CardContent className="flex flex-col gap-4 p-6">
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 !rounded-xl" />
                        <div className="flex flex-col gap-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                        </div>
                    </div>
                    <Skeleton className="h-9 w-32" />
                    <div className="flex gap-3">
                        <Skeleton className="h-8 w-28 !rounded-xl" />
                        <Skeleton className="h-8 w-20 !rounded-xl" />
                    </div>
                </CardContent>
            </Card>

            {/* Limits */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[0, 1].map((i) => (
                    <Card key={i}>
                        <CardContent className="flex flex-col gap-4 p-5">
                            <Skeleton className="h-8 w-8 !rounded-xl" />
                            <Skeleton className="h-8 w-24" />
                            <Skeleton className="h-1.5 w-full !rounded-full" />
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Billing + Invoices */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
                <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
            </div>
        </div>
    );
}

/* ─── Progress Bar ──────────────────────────────── */
function ProgressBar({ percent = 0, state = 'normal' }) {
    const clamped = Math.max(0, Math.min(100, percent));
    const color = { normal: 'var(--dash-primary)', warning: 'var(--dash-warning)', danger: 'var(--dash-danger)' }[state];
    return (
        <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'var(--dash-surface-subtle)' }}>
            <div
                className="h-full rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${clamped}%`, background: color }}
            />
        </div>
    );
}

/* ─── Limit Card ────────────────────────────────── */
function LimitCard({ icon: Icon, label, value, max }) {
    const hasMax = typeof max === 'number' && max > 0;
    const ratio = hasMax ? value / max : 0;
    const percent = hasMax ? Math.min(100, ratio * 100) : 0;
    const state = !hasMax ? 'normal' : ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warning' : 'normal';

    const valueText = (value ?? 0).toLocaleString('pt-br');
    const maxText = hasMax ? max.toLocaleString('pt-br') : '∞';

    return (
        <Card>
            <CardContent className="flex flex-col gap-4 p-5">
                <div className="flex items-center gap-2.5">
                    <div
                        className="flex h-8 w-8 items-center justify-center rounded-xl border"
                        style={{ background: 'var(--dash-icon-bg)', borderColor: 'var(--dash-border)' }}
                    >
                        <Icon size={14} style={{ color: 'var(--dash-text-soft)' }} />
                    </div>
                    <span className="text-xs font-medium" style={{ color: 'var(--dash-text-muted)' }}>
                        {label}
                    </span>
                </div>

                <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-semibold tabular-nums tracking-tight" style={{ color: 'var(--dash-text)' }}>
                        {valueText}
                    </span>
                    <span className="text-xs tabular-nums" style={{ color: 'var(--dash-text-faint)' }}>
                        / {maxText}
                    </span>
                </div>

                <div>
                    <ProgressBar percent={percent} state={state} />
                    {hasMax && (
                        <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                            <span>{Math.round(percent)}% usado</span>
                            {state === 'danger' && <span style={{ color: 'var(--dash-danger)' }}>Limite atingido</span>}
                            {state === 'warning' && <span style={{ color: 'var(--dash-warning)' }}>Próximo do limite</span>}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

/* ─── Invoice Row ───────────────────────────────── */
function InvoiceRow({ item }) {
    return (
        <div
            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors sm:px-4"
            style={{ borderBottom: '1px solid var(--dash-border)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dash-surface-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
            {/* Icon */}
            <div
                className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:flex"
                style={{ background: 'var(--dash-icon-bg)', border: '1px solid var(--dash-border)' }}
            >
                <FileText size={13} style={{ color: 'var(--dash-text-muted)' }} />
            </div>

            {/* Info */}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-xs font-medium" style={{ color: 'var(--dash-text)' }}>
                        {item.plan}
                    </span>
                    <span className="text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                        {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--dash-text)' }}>
                        R$ {item.value.toFixed(2).replace('.', ',')}
                    </span>
                    <Badge variant={statusVariants[item.status] || 'default'} dot>
                        {statusLabels[item.status] || item.status}
                    </Badge>
                </div>
            </div>

            {/* Action */}
            {item.pdfUrl && (
                <a href={item.pdfUrl} target="_blank" rel="noreferrer" className="shrink-0">
                    <Tooltip content="Baixar fatura">
                        <button
                            type="button"
                            className="flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200 hover:scale-105"
                            style={{
                                background: 'var(--dash-surface-subtle)',
                                border: '1px solid var(--dash-border)',
                                color: 'var(--dash-text-muted)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--dash-primary)';
                                e.currentTarget.style.borderColor = 'var(--dash-primary-border)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--dash-text-muted)';
                                e.currentTarget.style.borderColor = 'var(--dash-border)';
                            }}
                        >
                            <Download size={13} />
                        </button>
                    </Tooltip>
                </a>
            )}
        </div>
    );
}

/* ─── Invoice History ───────────────────────────── */
function InvoiceHistory() {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);

    const loadInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getInvoices();
            setInvoices(data);
        } catch (err) {
            console.error('Error to load invoices: ', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadInvoices();
    }, [loadInvoices]);

    return (
        <Card className="flex h-full flex-col">
            <div className="flex items-center justify-between px-6 pb-2 pt-6">
                <div className="flex items-center gap-2.5">
                    <div
                        className="flex h-8 w-8 items-center justify-center rounded-xl border"
                        style={{ background: 'var(--dash-icon-bg)', borderColor: 'var(--dash-border)' }}
                    >
                        <FileText size={14} style={{ color: 'var(--dash-text-soft)' }} />
                    </div>
                    <h3 className="text-sm font-semibold tracking-tight" style={{ color: 'var(--dash-text)' }}>
                        Histórico de Faturas
                    </h3>
                </div>
                {invoices.length > 6 && (
                    <button
                        type="button"
                        className="flex items-center gap-1 text-[11px] font-medium"
                        style={{ color: 'var(--dash-primary)' }}
                    >
                        Ver tudo <ChevronRight size={12} />
                    </button>
                )}
            </div>

            <CardContent className="flex flex-1 flex-col pt-2">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                ) : invoices.length === 0 ? (
                    <EmptyState
                        icon={FileText}
                        title="Nenhuma fatura"
                        description="Seu histórico de faturas aparecerá aqui."
                        className="py-10"
                    />
                ) : (
                    <div className="flex flex-col">
                        {/* Header */}
                        <div className="mb-1 hidden items-center gap-3 px-4 sm:flex">
                            <div className="w-8 shrink-0" />
                            <div className="flex flex-1 items-center gap-4">
                                <span className="flex-1 text-[10px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--dash-text-faint)' }}>
                                    Fatura
                                </span>
                                <span className="text-[10px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--dash-text-faint)' }}>
                                    Valor
                                </span>
                                <span className="w-[70px] text-[10px] font-medium uppercase tracking-[0.1em]" style={{ color: 'var(--dash-text-faint)' }}>
                                    Status
                                </span>
                            </div>
                            <div className="w-8 shrink-0" />
                        </div>
                        {invoices.slice(0, 6).map((item) => (
                            <InvoiceRow key={item.id} item={item} />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/* ─── Main Component ────────────────────────────── */
export default function SubscriptionManager() {
    const [details, setDetails] = useState(null);
    const [home, setHome] = useState({});
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);

    const { modalStore } = useStores();

    useEffect(() => {
        getSubscriptionDetails()
            .then((response) => {
                setDetails(response.data);
                setHome(response.home);
                setIsOwner(response.data.userRole === 'owner' || response.data.userRole === 'admin');
            })
            .catch((err) => console.error('Invalid server response on fetch user details', err))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <SubscriptionSkeleton />;

    const renewalDate = details
        ? new Date(details.renewalDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '...';

    const isCancelling = details?.cancelAt || details?.canceledAt;
    const price = details?.price?.toFixed(2)?.replace('.', ',');

    return (
        <div className="flex flex-col gap-5">
            {/* ── Plan Hero Card ────────────────────── */}
            <Card
                glow
                className="overflow-hidden"
            >
                {/* Gradient accent bar */}
                <div
                    className="h-1"
                    style={{ background: 'linear-gradient(90deg, #ad34fe 0%, #7b1fd4 50%, #ad34fe 100%)' }}
                />

                <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                        {/* Left */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <div
                                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                                    style={{
                                        background: 'var(--dash-primary-soft)',
                                        border: '1px solid var(--dash-primary-border)',
                                    }}
                                >
                                    <Crown size={18} strokeWidth={2} style={{ color: 'var(--dash-primary)' }} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold tracking-tight" style={{ color: 'var(--dash-text)' }}>
                                            {details?.planName}
                                        </h3>
                                        <Badge variant={isCancelling ? 'warning' : 'success'} dot>
                                            {isCancelling ? 'Cancelando' : 'Ativo'}
                                        </Badge>
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-2">
                                        <CalendarClock size={12} style={{ color: 'var(--dash-text-faint)' }} />
                                        <span className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                                            {isCancelling ? `Expira em ${renewalDate}` : `Renova em ${renewalDate}`}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Price */}
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold tabular-nums tracking-tight" style={{ color: 'var(--dash-text)' }}>
                                    R$ {price}
                                </span>
                                <span className="text-xs font-medium" style={{ color: 'var(--dash-text-faint)' }}>
                                    /mês
                                </span>
                            </div>

                            {/* Features */}
                            {details?.features?.length > 0 && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                                    {details.features.map((feature, i) => (
                                        <span key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dash-text-soft)' }}>
                                            <Check size={12} strokeWidth={2.5} style={{ color: 'var(--dash-primary)' }} />
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right — Actions */}
                        <div className="flex shrink-0 gap-2">
                            <Tooltip content={!isOwner ? 'Somente donos podem alterar o plano' : ''}>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={ArrowUpRight}
                                    disabled={!isOwner}
                                    onClick={() => modalStore.openModal(ModalEnum.UPGRADE_PLAN)}
                                >
                                    Mudar plano
                                </Button>
                            </Tooltip>
                            <Tooltip content={!isOwner ? 'Somente donos podem cancelar' : ''}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    leftIcon={Ban}
                                    disabled={!isOwner}
                                    onClick={() => modalStore.openModal(ModalEnum.CANCEL_PLAN)}
                                >
                                    Cancelar
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Plan Limits ───────────────────────── */}
            <div>
                <h3 className="mb-3 text-sm font-semibold tracking-tight" style={{ color: 'var(--dash-text)' }}>
                    Limites do plano
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <LimitCard icon={Bot} label="Agentes de IA" value={home?.assistants?.value} max={home?.assistants?.max} />
                    <LimitCard icon={MessageSquare} label="Conversas / mês" value={home?.conversations?.value} max={home?.conversations?.max} />
                </div>
            </div>

            {/* ── Billing + Referral / Invoices ─────── */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Left stack */}
                <div className="flex flex-col gap-4">
                    {/* Billing */}
                    <Card>
                        <CardContent className="p-5 sm:p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div
                                        className="flex h-8 w-8 items-center justify-center rounded-xl border"
                                        style={{ background: 'var(--dash-icon-bg)', borderColor: 'var(--dash-border)' }}
                                    >
                                        <CreditCard size={14} style={{ color: 'var(--dash-text-soft)' }} />
                                    </div>
                                    <h3 className="text-sm font-semibold tracking-tight" style={{ color: 'var(--dash-text)' }}>
                                        Detalhes de Cobrança
                                    </h3>
                                </div>
                                <Tooltip content="Em breve">
                                    <Button variant="ghost" size="icon-sm" disabled>
                                        <ChevronRight size={14} />
                                    </Button>
                                </Tooltip>
                            </div>

                            <div className="mt-4 flex flex-col gap-3">
                                {/* Card info */}
                                <div
                                    className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                                    style={{ background: 'var(--dash-surface-subtle)', border: '1px solid var(--dash-border)' }}
                                >
                                    <div
                                        className="flex h-8 w-12 items-center justify-center rounded-lg"
                                        style={{
                                            background: 'linear-gradient(135deg, #ad34fe 0%, #7b1fd4 100%)',
                                        }}
                                    >
                                        <CreditCard size={14} style={{ color: '#fff' }} />
                                    </div>
                                    <div>
                                        <span className="text-xs font-medium" style={{ color: 'var(--dash-text)' }}>
                                            {details?.billing?.cardType} •••• {details?.billing?.last4}
                                        </span>
                                    </div>
                                </div>

                                {/* Name & Address */}
                                <div className="flex flex-col gap-1 px-1">
                                    <span className="text-xs font-medium" style={{ color: 'var(--dash-text-soft)' }}>
                                        {details?.billing?.name}
                                    </span>
                                    {details?.billing?.address && (
                                        <address
                                            className="text-[11px] not-italic leading-relaxed whitespace-pre-line"
                                            style={{ color: 'var(--dash-text-muted)' }}
                                        >
                                            {details.billing.address}
                                        </address>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Referral */}
                    <Card
                        className="overflow-hidden"
                        style={{
                            background: 'var(--dash-primary-soft)',
                            borderColor: 'var(--dash-primary-border)',
                        }}
                    >
                        <CardContent className="p-5 sm:p-6">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                                <div
                                    className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                                    style={{
                                        background: 'var(--dash-surface)',
                                        border: '1px solid var(--dash-border)',
                                    }}
                                >
                                    <LogoSVG style={{ width: 32, height: 32, transform: 'scale(0.85)' }} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold" style={{ color: 'var(--dash-text)' }}>
                                        Ganhe 1 mês grátis
                                    </h4>
                                    <p className="mt-0.5 text-xs leading-relaxed" style={{ color: 'var(--dash-text-muted)' }}>
                                        Convide uma empresa para um plano pago da Verboo e ganhe 30 dias gratuitos.
                                    </p>
                                </div>
                                <Tooltip content="Em breve">
                                    <Button variant="outline" size="sm" leftIcon={Gift} disabled>
                                        Convidar
                                    </Button>
                                </Tooltip>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Right — Invoice History */}
                <InvoiceHistory />
            </div>
        </div>
    );
}
